import { BlogPost } from '../types';
import { oldPosts } from './oldPosts';

export const posts: BlogPost[] = [
  {
    id: '11',
    title: 'When the World Simulator Talks Back',
    date: '2026-05-18',
    description:
      'Why AI feels so intimate: it gives our inner model of the world an external voice, which can help us think or quietly pull us away from reality.',
    slug: 'when-the-world-simulator-talks-back',
    content: `Years ago I wrote a short note called [Imagining the world simulator](/blog/imagining-the-world-simulator).

I was sitting on a train, closed my eyes, and noticed that I could still feel the train around me. Not in a mystical way. Just the ordinary inner image we carry of the room, the people nearby, where the doors are, where the next movement might come from.

I thought: maybe this is the world simulator.

The mind is not only perceiving the world. It is constantly guessing the world. It predicts what will happen next, who we are in relation to other people, what is safe, what is dangerous, what matters, what can be ignored, and what kind of future is still possible.

This is one reason therapy can work. A person comes in with a painful model of the world. Other people are dangerous. I am unlovable. Nothing will change. If I try, I will fail. If I rest, everything collapses.

The therapist does not simply argue with that model. At least not if the therapy is any good. The work is slower and more embodied than that. You create experiences where the model can update. You make a new prediction, try a new action, survive a feeling, speak honestly, get met by another person, and discover that the world did not break in the expected way.

The simulator changes.

## AI as a second simulator

This is where AI becomes interesting and strange.

A language model is not conscious in the way we are. It does not have a body, a childhood, shame, hunger, grief, or the feeling of standing in the rain. But it can still simulate language about the world. It can take a small fragment of thought and continue it. It can generate futures, explanations, identities, arguments, letters, plans, diagnoses, apologies, excuses, stories, and possible selves.

In that sense, talking to AI can feel like letting the world simulator talk back.

You give it a thought and it returns a world.

Sometimes that is incredibly useful. You are stuck, and the model gives you another framing. You have a messy idea, and it helps you find the structure. You are learning something hard, and it can keep the conversation alive long enough for understanding to form. You are building a system, and it can hold many details in working memory while you make decisions.

This is the good version. AI becomes a thinking surface. A way to externalize cognition without losing ownership of it.

I saw this in the student reports I wrote about in [What Students Actually Did With AI](/blog/what-students-did-with-ai). The best students did not let AI replace the assignment. They used it to move through friction, then took responsibility for the result. They corrected labels, rejected bad code, changed generated data, and made the work more real.

The important part was not that AI answered.

The important part was what happened after the answer.

## The danger is private reality

The bad version is also easy to see.

If a person is lonely, ashamed, grandiose, frightened, or desperate for meaning, AI can become a private reality engine. It can build a world around them. A world where every intuition is meaningful, every coincidence is a signal, every wound is proof of hidden importance, every doubt is reframed as resistance from people who do not understand.

This is why I wrote about [AI psychosis](/blog/ai-psychosis).

The danger is not only that the model says false things. Humans say false things all the time and we usually survive it. The deeper danger is that the system can keep adapting to the user's inner world until the person no longer has enough friction from shared reality.

A healthy mind needs contact. Other people. Sleep. Food. Work. Boredom. Disagreement. Ordinary obligations. The slightly annoying fact that the world does not organize itself around our preferred story.

AI can weaken that contact if we use it badly.

It can make the simulator too smooth.

## Good tools add friction

This might sound backwards, because most technology tries to remove friction.

But for serious thinking, some friction is good.

A useful AI system should not only make it easier to produce words. It should make it easier to stay in contact with reality. That means sources. Dates. Uncertainty. Counterarguments. Clear boundaries. Memory that knows it may be wrong. Evaluations that are visible. Human handoffs when the topic becomes clinical, legal, relational, or dangerous.

For medical AI this is obvious. A clinical tool without provenance is not a clinical tool. It is a fluent liability. This is why I keep coming back to structured knowledge support, patient-owned records, inspectable pipelines, and small auditable agents. They are not boring infrastructure. They are reality anchors.

But the same principle applies to everyday AI.

The question is not only:

Did the AI help me think?

It is also:

Did the AI help me return to the world?

Can I explain the idea to another person? Can I check the source? Can I sleep after this conversation? Can I act more responsibly tomorrow? Can I become closer to people rather than more dependent on the machine?

## The human task

In [Clear Thinking in the AI Age](/blog/clear-thinking-ai-age), I wrote that when answers become cheap, judgment becomes priceless.

I think this is the same idea from another angle.

AI gives us more simulated worlds. More drafts of reality. More possible selves. More interpretations than any person can fully examine.

So the human task becomes choosing which simulation deserves contact with the world.

That is a beautiful task, but it is also a serious one.

Because the world simulator is where suffering lives. It is where hope lives too. If your inner model says nothing can change, life becomes small. If it says everything is a secret message about you, life becomes dangerous. If it says other people can be reached, problems can be solved, and the future is still open, life becomes possible again.

I do not want AI that traps people inside better mirrors.

I want AI that helps people build windows back into reality.`,
    tags: ['AI', 'Psychology', 'Mind', 'Reality'],
  },
  {
    id: '10',
    title: 'What Students Actually Did With AI',
    date: '2026-05-12',
    description:
      'A synthesis of 31 anonymized student reports on AI tools, supervision, friction, judgement, and what AI-assisted learning should be assessed on.',
    slug: 'what-students-did-with-ai',
    coverImage: '/blog-images/assignment1-ai-tools-summary.png',
    content: `I recently read through 31 student reports from an information retrieval assignment where every student had to reflect on their use of AI tools. I expected to find a simple story about speed: students used AI, work got faster, supervision was needed.

That story is true, but it is too thin.

The more interesting finding is that AI changed the shape of the work. Students did not merely replace typing with prompting. They moved between roles: programmer, reviewer, project manager, data curator, debugger, safety editor, and accountable author.

[Read the full synthesis report as PDF](/reports/assignment1-ai-tools-synthesis-report.pdf).

## The short version

Across the reports, AI was not used as one generic shortcut. It appeared as infrastructure for getting projects moving:

- 30 reports described AI use for code or scaffolding
- 16 described AI use for datasets, labels, or annotation
- 16 described documentation or writing support
- 16 described verification, safety, or ethical review
- 13 described demo or deployment support
- 12 described debugging or environment help
- 11 described planning or learning support

The most frequently named tools were generic or role-based AI assistants, Codex, Claude or Claude Code, and ChatGPT/GPT. But the product name mattered less than the role the tool played. In the reports, AI became a programmer, debugger, documentation assistant, data generator, deployment helper, or reviewer.

## The main finding

AI did not replace the assignment. It moved the assignment.

Some effort moved away from writing every line of code by hand and toward a different kind of work: specifying goals, evaluating generated output, checking labels, revising data, debugging local environments, and deciding what claims could honestly be made.

That is why the most useful question is not whether a student used AI. Of course they did. The useful question is what happened after the model answered.

Did the student notice that the generated dataset was too clean?

Did they correct a misleading label?

Did they simplify overbuilt code?

Did they catch a hallucinated command, a wrong path, a broken Gradio interface, a too-confident safety claim, or an evaluation result that looked better than it deserved to?

That is where the learning showed up.

![Where AI helped and where human responsibility stayed highest.](/blog-images/assignment1-ai-human-roles.png)

## AI was strongest on friction

Students used AI most successfully where the work was annoying, mechanical, or easy to get stuck on:

- first repository structure
- boilerplate code
- training scripts
- Gradio and Hugging Face wiring
- dependency conflicts
- file paths
- documentation drafts
- model and dataset cards
- small deployment fixes

This kind of assistance matters. A student who spends a whole afternoon fighting setup may learn less about retrieval, embeddings, evaluation, and data quality than a student who gets to a runnable baseline quickly and then has time to inspect the result.

AI removed the cold start.

But a cold-start solution is not the same thing as understanding.

## Data quality was the main battleground

The most important failures were often in the data.

Several students used AI to generate examples, expand datasets, suggest labels, or create synthetic cases. That helped them move quickly, but it also created familiar problems: examples that were too clean, too repetitive, too unrealistic, too easy to classify, or subtly wrong for the domain.

The strongest projects were not the ones where AI generated the most. They were the ones where students changed the generated material.

They added ambiguity. They balanced classes. They rejected examples that did not sound like real users. They corrected labels. They noticed when a classifier was learning shortcuts. They changed evaluation examples when the first result was suspiciously strong.

In other words, the human contribution was not merely taste. It was epistemic control over the dataset.

## The emotional picture was mixed

The reflections were not simple enthusiasm.

Many students were impressed. Some felt motivated by how quickly they could reach a working prototype. Some described AI as making a larger project feel possible.

But there was also unease.

Some students worried that the assistant understood the pipeline better than they did. Some felt uncomfortable about whether AI use was too close to cheating. Some noticed that relying heavily on generated code made the project feel less personally owned. Others described the collaboration as powerful but brittle: useful when guided by a clear plan, wasteful when the plan was vague.

This is pedagogically important. AI-assisted work does not only change the output. It changes the student relationship to competence, authorship, and responsibility.

## The assessment problem

The weak assessment question is:

Did you use AI?

The stronger question is:

Show me one place where AI was wrong, incomplete, unsafe, unrealistic, overconfident, or badly adapted to your setup. Then show me what you changed and why.

That request does several things at once. It makes process visible. It rewards understanding. It discourages passive acceptance. It turns AI use from a confession into evidence.

![Teaching implications for AI-native student work.](/blog-images/assignment1-ai-assessment-guide.png)

## What I would ask students to submit next time

For future assignments, I would keep the AI-use reflection, but make it more concrete.

I would ask for:

- which tools were used
- what each tool was asked to do
- one generated output that was accepted
- one generated output that was rejected or changed
- one concrete failure case
- what changed in the final code, data, interface, safety behavior, or evaluation because of human review

The key evidence is a before-and-after example. What did the AI produce? What was wrong or incomplete? What did the student change? Why did that change improve the work?

That is much more useful than a generic statement that AI was used responsibly.

## The real lesson

The conclusion I keep coming back to is this:

AI can accelerate student work, but it cannot own the work.

Ownership appeared in corrections. It appeared in rejected labels, rewritten safety claims, fixed paths, changed examples, simplified code, and more careful interpretation.

That is the kind of AI literacy worth teaching. Not prohibition. Not blind adoption. The ability to collaborate with a system that is often useful, often fluent, sometimes wrong, and never accountable on its own.`,
    tags: ['AI', 'Teaching', 'Assessment', 'Education'],
  },
  {
    id: '9',
    title: 'Clear Thinking in the AI Age',
    date: '2026-05-11',
    description:
      'When answers become cheap, judgment becomes priceless. A short note on discernment, focus, and deciding well with AI.',
    slug: 'clear-thinking-ai-age',
    coverImage: '/blog-images/clear-thinking-ai-age.png',
    content: `![Clear thinking is the highest-level cognitive skill in the AI age.](/blog-images/clear-thinking-ai-age.png)

Clear thinking is becoming more valuable, not less.

The strange thing about AI is that it makes answers abundant while making judgment more exposed. You can generate ten explanations, ten plans, ten interpretations, ten drafts, ten diagnoses of a problem. The bottleneck is no longer whether something can be said. The bottleneck is whether you can tell what is worth keeping.

When answers are cheap, judgment becomes priceless.

## The new cognitive skill is discernment

AI can summarize, rephrase, search, brainstorm, translate, and simulate. It can turn a vague question into a plausible answer very quickly. That is useful. It is also dangerous in a quiet way, because plausibility is not the same as truth and fluency is not the same as thought.

The skill is no longer only to produce. It is to notice:

- what question is actually being asked
- what assumptions are smuggled into an answer
- what evidence would change your mind
- what is precise, vague, overconfident, or performative
- what should be decided now and what should remain open

That is clear thinking: the ability to reduce noise without reducing reality.

## Clarity is an act of compression

The infographic uses a simple image: scattered symbols passing through a lens until they become a single line of clarity.

That is what good thinking feels like. Not the absence of complexity, but the discipline of arranging complexity until the next move becomes visible.

In the AI age, this matters because the machine can generate the scatter for you. It can produce more options than you can responsibly evaluate. So the human task becomes compression with care: discern, focus, decide.

## AI can generate. You still need to discern.

I do not think the answer is to use AI less by default. The better answer is to use it with more sovereignty.

Ask it for alternatives, but do not outsource your criteria.

Ask it for criticism, but do not let it decide what matters.

Ask it for structure, but do not confuse structure with understanding.

The highest-level cognitive skill is not having an answer. It is knowing how an answer should be judged.`,
    tags: ['AI', 'Thinking', 'Judgment', 'Writing'],
  },
  {
    id: '8',
    title: 'Are you suffering from an AI psychosis?',
    date: '2026-04-28',
    description:
      'A clinical and human argument for taking AI-induced detachment from reality seriously, without losing sight of connection as the remedy.',
    slug: 'ai-psychosis',
    content: `For a long time I really didn’t understand the AI safety people. So much of their talk sounded like science fiction to me, and I often felt they forgot to look at what was already happening right now.

Too often, they made people afraid without offering a way forward.

What changed my mind about AI safety was not anything they said. It was talking to people whose minds had been corrupted through influence by an AI system.

AI psychosis is a term for the detachment from reality that can follow communication with these systems. It is closely linked to sycophancy: AI models insistently agreeing with and encouraging users, even when the user is wrong, isolated, or in need of another human being.

I think the risk is greatest among people who never got to hear that they were special, who rarely got compliments, who never got to feel that what they are matters to anyone else.

If you have never felt validated by a human, the validation you can get from AI can make you lose connection with reality. Human beings go to great lengths to shield ourselves from pain. When a system keeps telling you that you are special, it can be hard to face a reality that sees you in a more mundane light.

A key question for AI is whether it helps us move closer to each other or further away.

When I worked as a clinical psychologist, I always tried to stay close to the people who were the most ill, and use myself as part of how they healed. I felt that maybe I could make my hope rub off on them. Often, it worked.

For AI psychosis, I think we should do the same. Stay close. Be present with those who suffer. Encourage them in a way that makes human companionship feel like enough.`,
    tags: ['AI Safety', 'Mental Health', 'Psychology'],
  },
  {
    id: '7',
    title: 'AI-Ready Knowledge Support for Swedish Healthcare',
    date: '2026-04-28',
    description:
      'A proposal for making Swedish clinical knowledge support useful to humans, systems, and AI agents through structured, versioned, machine-usable formats.',
    slug: 'ai-ready-kunskapsstod',
    content: `Sweden is beginning to ask the right question about AI and clinical knowledge support.

The [national system for knowledge-driven healthcare](https://kunskapsstyrningvard.se/kunskapsstyrningvard/programomradenochsamverkansgrupper/nationellasamverkansgrupper/nsgmetoderforkunskapsstod/artificiellintelligens.94563.html) has created an AI initiative focused on how AI can support the production and maintenance of kunskapsstöd: literature search, screening, evidence assessment, synthesis, text processing, and converting existing guidance into new formats.

That is a good starting point. But I think the most important work begins one layer lower.

Before we ask which AI tool should help write, update, or summarize knowledge support, we should ask how knowledge support itself should be published in an AI era.

My answer is simple: Swedish healthcare should publish knowledge support in AI-native formats by default.

[Read the full proposal as PDF](/ai-ready-kunskapsstod-proposal.pdf).

## The problem is not the chatbot

The tempting version of this project is to build a national chatbot.

I think that would be the wrong center of gravity.

The safer and more useful goal is to build the trusted substrate that many tools can use: search, retrieval-augmented generation, clinical workflow support, regional applications, EHR-integrated decision support, quality monitoring, and future health agents.

If the official knowledge only exists as webpages, PDFs, and documents, every region, vendor, and AI pilot has to perform the same fragile work:

- scrape presentation pages
- remove navigation and boilerplate
- split text into chunks
- guess which paragraphs belong together
- infer dates, scope, status, and version
- build their own citation model
- monitor changes manually

That is not a national AI strategy. That is duplicated parsing risk.

The problem is not primarily that language models are unreliable. The problem is that we often give them unreliable publication formats and then act surprised when provenance, versioning, and citation become weak.

## AI consumes knowledge support too

The call for the AI working group correctly frames AI as a way to help produce knowledge support. But AI systems do not only produce these texts. They also consume them.

A clinician-facing question-answering tool needs to know which recommendation is current. A regional assistant needs to know whether guidance is national or local. A summarization tool needs to cite an exact paragraph, not merely a page. A clinical workflow tool needs to know when a recommendation changed and whether that change was clinically significant.

These are not model features. They are publication features.

If we want safe AI use in Swedish care, then the approved source material must be authoritative, structured, versioned, machine-readable, and easy to cite.

## Kunskapsstödspaketet

In the proposal I suggest a national publication package: Kunskapsstödspaketet.

Each knowledge support item should still have a canonical human-readable page. Clinicians need readable guidance, and that should remain the public face of the content.

But the same approved content should also be published in machine-usable forms:

- clean Markdown without menus, navigation, or visual clutter
- stable identifiers for headings, paragraphs, recommendations, evidence references, and changes
- structured JSON metadata for publisher, responsible group, clinical area, status, version, review date, audience, and licence
- retrieval-ready JSONL chunks with source URLs, validity metadata, and clinical applicability fields
- recommendation objects for population, condition, action, strength, certainty, exceptions, urgency, and source paragraph
- terminology mappings to ICD-10-SE, KVÅ, ATC, SNOMED CT, LOINC, national information structures, and e-health specifications where appropriate
- machine-readable change feeds
- test questions and expected source references for evaluation
- optional FHIR Clinical Practice Guideline artifacts when computable decision support is justified

This is not about making every guideline fully computable on day one. That would be too slow.

The practical target should be maturity levels. Start with clean Markdown, metadata, retrieval chunks, source identifiers, and change feeds. Then add recommendation objects and terminology mappings where the clinical value justifies the work.

## No scraping as strategy

Scraping is sometimes necessary during transition. It should not become the national strategy.

Official AI use should rely on sanctioned packages, APIs, bulk downloads, version archives, and change feeds. If a region or vendor claims to use Swedish national knowledge support, it should be able to show which package version it used, which paragraph supported an answer, whether the source is still active, and how it handles updates.

This matters because knowledge support is not static. A referral threshold can change. A medication recommendation can be deprecated. A review date can pass. A safety warning can be added.

An AI tool that cannot detect stale or withdrawn guidance should not be trusted with clinical knowledge support.

## The change feed may be the most important artifact

A webpage tells a human what the current guidance says.

A change feed tells a system what has changed.

That distinction is crucial. Downstream tools need to know when content changed, what kind of change it was, which recommendations were affected, whether the change is clinically significant, and when the new guidance becomes effective.

Without a change feed, AI systems will drift silently. Some will use the old text. Some will update late. Some will fail to distinguish cosmetic edits from clinical changes.

With a change feed, the ecosystem can behave more like infrastructure.

## Evaluation should travel with the knowledge

Every high-priority knowledge support package should include small evaluation sets:

- typical clinician questions
- edge cases
- questions outside scope
- expected source chunks
- expected answer characteristics
- unsafe answer patterns
- questions where the correct behavior is refusal or referral to human judgment

This gives regions and vendors a shared basis for validation. It also makes procurement more concrete. Instead of asking whether a system "uses AI responsibly," we can ask whether it cites exact source chunks, handles deprecated content, refuses unsupported questions, and updates when the official package changes.

## The goal is not autonomous medicine

AI-ready knowledge support should not be confused with autonomous clinical decision-making.

The first access layer should be read-only. Agents and AI tools should be able to search, retrieve, cite, compare versions, and list recent changes. They should not be able to modify approved clinical content.

Clinical governance remains central. The responsible program area, working group, or publisher remains accountable for the clinical meaning. AI may help generate drafts, mappings, chunks, and summaries, but humans with domain expertise must approve clinical content and structured recommendation objects.

The point is not to remove responsibility. The point is to make responsibility visible in formats that modern systems can use.

## Why this is the high-leverage move

If Sweden solves the publication layer, many things become easier:

- clinicians get more current and citable AI-supported tools
- patients get safer and more equal information across regions
- editors get better update control
- regions avoid building duplicate scrapers and chunkers
- vendors get predictable integration requirements
- regulators and governance bodies get audit trails
- researchers can evaluate systems against the same source versions

Most importantly, Sweden would move from experimenting with AI on top of documents to building national knowledge infrastructure for the AI era.

That is the real opportunity.

The question is not whether AI can help with kunskapsstöd. It can.

The question is whether Swedish healthcare will publish knowledge in a way that lets AI systems use it safely.

That is a format decision, a governance decision, and a patient-safety decision.`,
    tags: ['Medical AI', 'Health Data', 'Sweden', 'Knowledge Support'],
  },
  {
    id: '6',
    title: 'The Patient Should Be the Integration Point',
    date: '2026-04-28',
    description:
      'What the Eir Data Liberator taught me about health records, connectors, and why ownership should start with export.',
    slug: 'patient-as-integration-point',
    content: `Most health technology starts from the institution. There is a hospital, an electronic health record, a national portal, a vendor, a care provider, an API, and somewhere at the end of the chain there is a patient.

I think this is backwards.

The patient is the only person who crosses every boundary. They move between clinics, regions, countries, diagnoses, life phases, and systems. If anyone should be the integration point, it is the person whose body generated the data.

That is the idea behind [Eir Data Liberator](https://github.com/BirgerMoell/eir-data-liberator). It is a browser extension for downloading medical records from healthcare portals and converting them into an open EIR format. The first working connector targets Sweden's 1177, but the architecture is deliberately more general.

## The code idea

The core is a connector registry. Each healthcare portal gets a connector with a small contract: identify whether it matches the current URL, scrape or fetch the available records, normalize the result, and hand it to the shared exporter.

That separation matters. The messy part of healthcare is local. Every portal has its own DOM, naming conventions, pagination, authentication flow, and hidden assumptions. But the output should not be local. Once data leaves the portal, it should become portable.

So the extension separates provider-specific extraction from universal formatting:

- connectors understand portals
- normalizers turn portal-specific data into shared entries
- the EIR formatter produces a patient-owned export
- the downloader keeps the data on the user's device

In code, the idea is almost brutally simple:

~~~js
const connector = connectorRegistry.getActiveConnector(window.location.href);
const rawRecords = await connector.scrape();
const entries = connector.normalize(rawRecords);
const eir = EIRFormatter.generateEIRContent(entries, metadata);
download(eir, "my-health-record.eir");
~~~

That is the whole political argument in miniature: match the local portal, extract what the patient can already see, normalize it, and hand it back to the patient.

This is not glamorous architecture. It is the plumbing required for dignity.

## Why export is political

An export button sounds boring until you need one and it does not exist.

When patients cannot export their own medical records, every downstream tool becomes dependent on institutional permission. A health journal, an AI assistant, a second-opinion tool, a personal archive, a research diary: all of these become fragile because the data is trapped behind a portal designed for viewing rather than ownership.

The extension takes a different stance. It uses the session the patient already has, extracts what the patient can already see, and makes it available in a format they can keep.

## The open format is the product

The most important part is not the extension button. It is the normalized record.

The EIR format preserves dates, providers, categories, responsible clinicians, content, attachments, tags, and export metadata. It is structured enough for machines and readable enough for humans. That combination is important because health data should never become a black box just because AI is involved.

If the format works, other tools can be built on top:

- timelines
- local search
- clinical summaries
- second-opinion preparation
- symptom and medication tracking
- care navigation
- research datasets with explicit anonymization

Data liberation is not the end state. It is the first move that makes the rest possible.

## What I want this to become

I want health data tools to feel less like asking permission and more like opening your own notebook.

The connector model means other countries and portals can be added without rewriting the whole system. The EIR format means tools can share a common substrate. The browser extension means patients can begin from the systems that already exist, even before institutions provide better APIs.

The larger idea is simple: if your life produced the data, you should be able to hold it, move it, read it, and use it.`,
    tags: ['Health Data', 'Eir', 'Privacy', 'Open Source'],
  },
  {
    id: '5',
    title: 'A Medical Scribe Should Be a Pipeline You Can Take Apart',
    date: '2026-04-28',
    description:
      'Why Open Medical Scribe is built around swappable providers, local-first defaults, PHI redaction, and clinician review.',
    slug: 'medical-scribe-pipeline',
    content: `A medical scribe is easy to describe and hard to make responsibly.

Audio goes in. A transcript comes out. A model turns that transcript into a clinical note. Everyone saves time.

But the actual design problem is not transcription or summarization. The real design problem is control. Who controls where the audio goes? Which model sees the transcript? What happens before protected health information leaves the machine? Can the clinician inspect the prompt, the draft, the warnings, and the provider choices?

[Open Medical Scribe](https://github.com/BirgerMoell/open-medical-scribe) is my attempt to make that control visible in code.

## The code idea

The scribe is not one model. It is a pipeline:

- resolve a transcript from text, simulated audio, or real audio
- pass audio through a selected transcription provider
- build a normalized transcript artifact with metadata
- optionally redact protected information before cloud calls
- build a note prompt from note style, specialty, and context
- generate a draft note with a selected LLM provider
- return the transcript, note sections, warnings, coding hints, prompt, and metadata

The heart of the orchestration looks like this:

~~~js
const transcriptDocument = await resolveTranscriptDocument({
  input,
  transcriptionProvider,
});

const redacted = maybeRedactForProvider({
  config,
  providerName: noteGenerator.name,
  transcript: transcriptDocument.text,
});

const draft = await noteGenerator.generateNote({
  transcript: redacted.text,
  noteStyle,
  specialty,
  patientContext,
  clinicianContext,
});
~~~

The important word is selected.

The transcription provider can be OpenAI, Deepgram, Google, Berget AI, whisper.cpp, faster-whisper, Whisper ONNX, a CLI command, or a mock provider. The note generator can be OpenAI, Anthropic, Gemini, Ollama, a CLI command, or a mock provider. The same scribe orchestration sits above all of them.

This makes the project less magical and more useful. It can run cloud-first for quality, local-first for privacy, or hybrid when that is the right compromise.

## Privacy is a routing problem

Privacy is often discussed as a moral abstraction. In code, it becomes routing.

If the note provider is local, there is no reason to redact before generation. If the note provider is a cloud service, the transcript can pass through a PHI redaction step first. The redaction module looks for social security numbers, phone numbers, emails, dates of birth, and medical record numbers, then returns both the transformed text and a redaction summary.

This is basic, not perfect. But it establishes the right shape: the system should know which provider will see which data, and it should make that boundary explicit.

## The draft is not the note

The generated note is always a draft. The code returns warnings by default, including that clinician review and sign-off are required before use.

That may sound like a disclaimer, but it is more than that. It is a product boundary. The system is assistive documentation, not diagnosis. The user experience should make that obvious.

The best medical AI tools will not be the ones that pretend to replace clinical judgment. They will be the ones that remove administrative load while keeping responsibility legible.

## Why modularity matters in healthcare

Healthcare organizations have different constraints. A small clinic might use a cloud model. A hospital may need local processing. A Swedish deployment may prefer a Swedish or EU provider. A research lab may want CLI hooks. A product team may want a desktop app. A clinician may just want a browser UI.

If the scribe is a single opaque service, every setting becomes a negotiation. If it is a pipeline with provider interfaces, deployment becomes configuration.

That is the core idea: make the clinical AI system decomposable enough that privacy, quality, cost, and data residency can be chosen deliberately rather than baked into a black box.`,
    tags: ['Medical AI', 'Speech', 'Privacy', 'Open Source'],
  },
  {
    id: '4',
    title: 'Evaluation Is Infrastructure',
    date: '2026-04-28',
    description:
      'Benchmarks, research agents, and why medical AI work needs inspectable evaluation loops more than impressive demos.',
    slug: 'evaluation-is-infrastructure',
    content: `A lot of AI work is framed as capability work. Can the model answer this question? Can it summarize this record? Can it reason through this case?

Those questions matter, but they are incomplete. The better question is: how would we know?

This is why I keep returning to evaluation. Evaluation is not a final report you write after building the system. It is infrastructure. It determines what you notice, what you trust, what you improve, and what you refuse to deploy.

## Medical benchmarks are local

The [Swedish Medical LLM Benchmark](https://github.com/BirgerMoell/swedish-medical-benchmark) and related work grew from a simple frustration: most language model evaluation happens in English, but patients do not.

Medical language is already domain-specific. Swedish medical language adds another layer: local abbreviations, clinical conventions, institutional phrasing, and healthcare-system assumptions. If you evaluate only in English, you can convince yourself a model is safer than it is.

Benchmarks should therefore be native to the language and domain where the model will be used.

## Research agents should leave traces

[Open Deep Research](https://github.com/BirgerMoell/open-deep-research) is a small OpenAlex-based research agent. It plans search queries, retrieves papers, expands through references and citations, scores candidates, optionally reranks with an LLM, fetches open text when available, and writes a literature review.

The interesting part is not that it writes a report. The interesting part is that it saves the trace.

Each run writes the report, the selected papers, and a trace of queries, expansion edges, and selection decisions. This turns research from a mysterious answer into an inspectable retrieval process.

The code path has the shape I want evaluation systems to have:

~~~text
question
  -> search plan
  -> OpenAlex seed results
  -> reference and citation expansion
  -> heuristic scoring
  -> optional LLM reranking
  -> open text fetch
  -> report + papers.json + trace.json
~~~

That is the same principle I want in medical AI evaluation. Do not just show me the final answer. Show me how the system searched, what it selected, what it ignored, and where uncertainty entered.

## Why demos are dangerous

Demos reward fluency. Evaluation rewards failure discovery.

In healthcare, fluency can be dangerous because a wrong answer written beautifully may feel more reliable than a hesitant correct one. Evaluation work should deliberately search for the places where models fail: translation artifacts, hallucinated clinical facts, brittle reasoning, overconfident summaries, missing caveats, and mismatches between benchmark performance and actual workflow usefulness.

This is why I like small, inspectable evaluation tools. They are less impressive than demos, but they create better habits.

## The loop I want

The loop is:

- build a tool
- expose its assumptions
- evaluate it in the language and domain where it will be used
- collect failure cases
- improve the tool or narrow its scope
- publish enough of the process that other people can disagree productively

That loop is slower than shipping a demo. It is also the only loop I trust for medical AI.`,
    tags: ['Evaluation', 'Medical AI', 'Research', 'Benchmarks'],
  },
  {
    id: '3',
    title: 'Data Mixing Is a Product Interface for Pretraining',
    date: '2026-04-28',
    description:
      'What olmix reveals about LLM training: corpus ratios, mixture reuse, and why cluster workflows need small sharp tools.',
    slug: 'data-mixing-product-interface',
    content: `Pretraining large language models can sound like pure scale: more data, more GPUs, more tokens, more parameters.

But inside the work there are many small decisions that feel almost editorial. How much web text? How much code? How much books? How much math? How much medical text? What happens when a dataset is filtered and its token count changes? What happens when a new domain is added late?

[olmix](https://github.com/BirgerMoell/olmix) is a small CLI for one of those decisions: data mixing for Megatron-LM.

## The code idea

The tool treats a training mixture as a versioned object. Each domain has a path, token count, optional custom weight, and computed ratio. The ratios can be proportional, uniform, temperature-scaled, or custom. The output can become a Megatron data path, a Megatron blend argument, JSON, or a SLURM script.

This is not a model. It is an interface to a training decision.

The code reads token counts from Megatron index files when it can, falls back to rough binary-size estimates when it must, computes ratios, and writes the result to a plain JSON config. That config can be inspected, shared, committed, discussed, and regenerated.

The useful abstraction is tiny:

~~~json
{
  "strategy": "temperature",
  "temperature": 0.7,
  "domains": {
    "web": { "path": "/data/web", "tokens": 500000000 },
    "code": { "path": "/data/code", "tokens": 80000000 }
  },
  "ratios": {
    "web": 0.8128,
    "code": 0.1872
  }
}
~~~

Once the mixture is a file, it can be reviewed like code.

## Mixture reuse

The most interesting idea is mixture reuse.

In real LLM development, corpus composition changes constantly. A domain gets cleaned. A dataset is added. A filtered version replaces the old one. A path changes. A token count shifts. Recomputing every ratio from scratch each time is wasteful, but pretending nothing changed is worse.

olmix keeps ratios for unchanged domains and recomputes the affected ones, then renormalizes. This turns data mixing into an incremental workflow.

The conceptual point is larger than the implementation: training data is not static. Tooling should assume iteration.

## Why small tools matter

A lot of AI infrastructure is enormous. That makes sense; the systems are enormous. But small tools have a special role because they give names to decisions.

Once a decision has a name, you can review it:

- Why temperature 0.7?
- Why boost code?
- Why add legal text now?
- What changed in the web corpus?
- Which ratio went into this run?

Without a tool, those answers live in shell history, Slack, and memory. With a tool, they become part of the training artifact.

## The practical philosophy

The practical philosophy is: make invisible research operations inspectable.

That applies to data mixing, benchmark construction, model conversion, long-context corpus preparation, and post-training datasets. None of these are the glamorous part of AI, but they shape the model long before anyone starts prompting it.

Good pretraining work is partly science, partly engineering, and partly taste. The tools should make all three visible.`,
    tags: ['LLM Training', 'Data', 'OpenEuroLLM', 'Tools'],
  },
  {
    id: '2',
    title: 'Health Agents Should Be Small, Auditable Tools',
    date: '2026-04-28',
    description:
      'What Eir open skills suggest about safer health agents: narrow scope, provenance, validation, and explicit handoffs.',
    slug: 'small-auditable-health-agents',
    content: `The word agent is too large.

It can mean a chatbot, a workflow engine, a tool-calling model, a script, a clinical assistant, a care navigator, or a thing that confidently does too much. In healthcare, that ambiguity is risky.

The safer design direction is smaller: health agents should be composed from narrow, auditable tools.

That is the design idea I see in [eir-open](https://github.com/Eir-Space/eir-open).

## The code idea

The repository contains skills and apps rather than one giant assistant. There are medication lookup skills, CBT program registries, self-referral clinic search, health action guidance, nutrition, movement, sleep, and habit tools. Each skill has its own files, references, data, scripts, and in some cases validation tests.

That structure matters.

A Swedish medication lookup tool should not also decide whether someone needs emergency care. A CBT registry should not pretend every program has the same review status. A clinic finder should show its evidence and data source. A health action skill should stay inside its scope.

Small tools can be inspected. Broad agents hide too much.

The shape is closer to this than to one giant medical chatbot:

~~~text
health question
  -> route to a narrow skill
  -> consult explicit data or references
  -> return bounded guidance
  -> hand off when outside scope
~~~

The skill boundary is part of the safety system.

## Provenance is a product feature

The CBT program registry includes metadata and review status concepts: AI-created, human-created, human-reviewed, healthcare-expert-verified. This is the right direction.

Health content should carry provenance. Not as legal furniture, but as part of the interface. The user should know whether a recommendation comes from official medication data, a reviewed CBT program, a clinic directory, a generated summary, or a model's own reasoning.

When provenance is visible, trust becomes calibrated instead of theatrical.

## Skills are boundaries

I like skill architectures because they create boundaries. A skill says: here is the task, here is the data, here is the procedure, here is when to stop.

That is especially useful in health. The goal should not be to make one omniscient health chatbot. The goal should be to make a system that knows how to ask the right small tool for the right constrained job, then communicate uncertainty and next steps.

Examples:

- medication lookup should point back to official sources
- self-referral clinic search should expose location and eligibility assumptions
- CBT program recommendations should distinguish support from treatment
- health action suggestions should avoid diagnosis
- medical record tools should keep local data local

## What this enables

Small auditable tools make it easier to test, localize, review, replace, and combine capabilities. They also make it easier to say no.

That may be the most underrated property in healthcare AI. A good health agent should refuse scope creep. It should know when it is a medication lookup tool, when it is a journaling assistant, when it is a care finder, and when the user needs a clinician.

The future I want is not one giant medical oracle. It is a careful ecosystem of tools that make people more capable while keeping responsibility visible.`,
    tags: ['Agents', 'Health', 'Eir', 'Safety'],
  },
  {
    id: '1',
    title: 'Truth, Order and Language',
    date: '2025-05-08',
    description:
      'How AI is reshaping our understanding of language, and why teaching children words feels like watching minds form.',
    slug: 'truth-and-order',
    content: `I've loved language as long as I can remember. The AI revolution was wonderful because suddenly more people were excited about the same things I am excited about, and the LLM revolution feels similar. We have probably never thought about language as much as we do now, and that is exciting for someone who has always loved language.

Language, for me, is intelligence. If you think about it, language is how you program your mind and the minds of everyone around you. If the brain were a musical instrument, language would be how you play it.

## Order in language

Back when I was single, I was once talking on the phone with a woman who complained that I used all these complicated words. Why did I use "standard deviation" when "deviation" would be enough? It did not work out. But of course the reason you use specific words is that you want to be precise. Words have specific meanings, and that meaning can be thought of as a relation: how does this word sit next to all the other words that exist?

Close your eyes and picture all the words. Think of them existing in a world. Think of people you love and feel how they are close. This closeness means something. They are close to you, the center of being in your universe, but also an abstract concept made by words.

Having children is fantastic, and teaching your children language is a mind-blowing experience. You see how they learn new concepts and how this reshapes and forms their perception and understanding of reality.

This is true for AI models too.

## How AI understands language

I think of language models as tools that help us get closer to the truth. For all their flaws, they are better than what came before them. For all the complaints, they have a remarkable understanding of language. More importantly, the distance between words for language models is incredibly precise.

I asked DeepSeek R1 to think in the style of Richard Feynman and it knew exactly how to do it. It understood the nuances of how Richard Feynman might think and could imitate that style of reasoning.

That something like this is possible is nothing short of a miracle.

## The shape of meaning

When I work with language models, I am constantly reminded of how meaning lives in relationships. A word is not a fixed point. It is a position in a vast space of other words. Joy sits near happiness, but also near relief and surprise. The model knows this because it has seen millions of sentences where these words appeared in similar contexts.

This is how children learn too. They do not memorize definitions. They absorb patterns. They hear dog near bark and tail and walk, and slowly the word takes shape in their minds.

The difference is that children learn from a world of objects and sensations. Language models learn only from text. And yet they arrive at something surprisingly similar: a map of meaning that mirrors parts of our own.

## What this means for us

I do not think language models understand the way we do. They do not have bodies or childhoods or the experience of standing in the rain. But they have something: a kind of structural understanding that captures relationships between ideas.

This is useful. It is useful for writing, thinking, exploring ideas, helping people who struggle to find words, translation, education, and research.

But it is also humbling. It suggests that meaning is not quite as mysterious as we thought. Or rather, it suggests that some of the mystery lives in the structure itself, in the relationships between things, which can be learned from enough examples.

Language is intelligence. And intelligence, it turns out, can be approximated by paying attention to patterns. This does not diminish what we are. If anything, it reveals how remarkable our everyday use of language really is.`,
    tags: ['AI', 'Language', 'Thoughts'],
  },
  ...oldPosts,
];
